/* ===========================================================================
  Translates kit variant-picker events into Turbo's `variant-change` CustomEvent,
  which `window.productPage.runOptionSelector` (sections.js) listens for.
============================================================================ */

// eslint-disable-next-line import/no-unresolved
import 'kit.product-variant-picker';

const INLINE_QUICKSHOP_SECTION = 'inline-quickshop';

function retargetInlineQuickshop(form) {
  if (!form.closest('.inline-quickshop')) return;

  const picker = form.querySelector('kit-variant-picker');

  if (!picker) return;

  picker.dataset.sectionId = INLINE_QUICKSHOP_SECTION;
}

function readProductPayload(form) {
  try {
    return JSON.parse(form?.dataset.product ?? 'null');
  } catch (error) {
    console.error('Could not parse the product payload.', error);
    return null;
  }
}

// BSF-CUSTOM · disable-all-atc · since 2026-09-21: vendor disabled only the FIRST .add_to_cart.
// On the ITORIS DPO products that is DPO's hidden original; the visible button is DPO's clone,
// which stayed clickable during the fetch while [name="id"] still held the OLD variant (and the
// sticky bar clicks the visible one). selectCallback already re-enables every .add_to_cart.
function disableWhileInFlight(form) {
  form.querySelectorAll('.add_to_cart').forEach(addToCart => {
    addToCart.classList.add('disabled');
    addToCart.setAttribute('disabled', 'disabled');
  });
}

// Kit's radio has no tooltip slot, so the theme applies the legacy
// `.swatch .tooltip` from the value name kit renders visually hidden.
const tooltipObserved = new WeakSet();

function applySwatchTooltips(form) {
  form
    .querySelectorAll('kit-variant-picker [data-option-value-id]:not([data-swatch-tooltip])')
    .forEach(label => {
      if (!label.querySelector('.kit-swatch')) return;

      const text = label.querySelector('.kit-visually-hidden')?.textContent.trim();

      if (text) label.setAttribute('data-swatch-tooltip', text);
    });
}

// Re-applied because kit swaps the picker's innerHTML on every selection.
function watchSwatchTooltips(form) {
  applySwatchTooltips(form);

  if (tooltipObserved.has(form)) return;

  tooltipObserved.add(form);

  new MutationObserver(() => applySwatchTooltips(form))
    .observe(form, { childList: true, subtree: true });
}

function read(form) {
  retargetInlineQuickshop(form);
  watchSwatchTooltips(form);

  const product = readProductPayload(form);
  const blob = form?.querySelector('[data-kit-selected-variant]');
  let variant = product?.variants?.[0] ?? null;

  if (blob) {
    try {
      variant = JSON.parse(blob.textContent?.trim() ?? 'null');
    } catch (error) {
      console.error('Could not parse the selected variant JSON.', error);
    }
  }

  return { product, variant };
}

window.kitVariantPicker = {
  read,
};

if (!window.__kitVariantPickerBridge) {
  window.__kitVariantPickerBridge = true;

  const settle = (form, picker, variant) => {
    const image = variant && variant.featured_image && variant.featured_image.src;

    // BSF-CUSTOM · bridge-swap-guard · since 2026-09-21: swapCardImage is defined late in app.js's
    // init handler; if it is missing or throws, `variant-change` must still dispatch, because that is
    // what re-enables Add to Cart (disabled by disableWhileInFlight above).
    try {
      if (image && typeof window.swapCardImage === 'function') window.swapCardImage(picker.closest('.thumbnail'), image);
    } catch (error) {
      console.error('[BSF] swapCardImage failed', error);
    }

    form.dispatchEvent(new CustomEvent('variant-change', {
      bubbles: true,
      detail: {
        product: readProductPayload(form),
        variant,
      },
    }));
  };

  // BSF-CUSTOM · instant-variant · since 2026-09-29
  // WHAT:  when the tapped combination resolves to a variant of this product's own JSON, point the
  //        form's name="id" at it and dispatch `variant-change` right away (price, button, per-unit
  //        line and captions update at once) instead of disabling Add to Cart for the section fetch.
  //        The fetch still runs and kit's after-change dispatches `variant-change` again with the
  //        server's answer; every listener is idempotent.
  // WHY:   Add to Cart sat disabled for the round trip (~0.3-0.9 s on phones) after every tap.
  // MERGE: falls back to vendor's disable-while-in-flight for incomplete selections, combined
  //        listings (variant not in this product), and ITORIS DPO pages (their pricing hooks run on
  //        variant-change and are not proven safe to run twice per tap).
  function settleEarly(form, detail) {
    try {
      if (!detail || detail.selectionIncomplete) return false;
      if (document.getElementById('itoris_dynamicproductoptions')) return false;
      const product = readProductPayload(form);
      if (!product || !Array.isArray(product.variants) || !Array.isArray(product.options)) return false;
      const chosen = {};
      (detail.selectedOptions || []).forEach(o => { chosen[o.name] = o.value; });
      const names = product.options.map(o => (typeof o === 'string' ? o : o && o.name));
      if (names.some(n => !(n in chosen))) return false;
      const variant = product.variants.find(v => names.every((n, i) => (v.options || [])[i] === chosen[n]));
      if (!variant) return false;
      form.querySelectorAll('input[name="id"]').forEach(input => { input.value = variant.id; });
      form.dispatchEvent(new CustomEvent('variant-change', { bubbles: true, detail: { product, variant } }));
      return true;
    } catch (error) {
      console.error('[BSF] instant variant update failed; waiting for the server', error);
      return false;
    }
  }

  document.addEventListener('kit:variant-picker:change', event => {
    const form = event.target.closest('[data-product-form]');

    if (!form) return;
    if (settleEarly(form, event.detail)) return;
    disableWhileInFlight(form);
  });
  // /BSF-CUSTOM · instant-variant

  document.addEventListener('kit:variant-picker:after-change', event => {
    const form = event.target.closest('[data-product-form]');

    if (!form) return;

    const { variant, productChanged } = event.detail;
    const picker = event.target;

    // Combined listings
    if (productChanged) {
      const productUrl = new URL(picker.dataset.productUrl, window.location.origin);
      const targetUrl = window.location.pathname.includes('/products/')
        ? new URL(window.location.href)
        : productUrl;

      targetUrl.pathname = productUrl.pathname;

      if (picker.dataset.variantId) {
        targetUrl.searchParams.set('variant', picker.dataset.variantId);
      }

      window.location.assign(targetUrl.toString());
      return;
    }

    settle(form, picker, variant);
  });

  document.addEventListener('kit:variant-picker:error', event => {
    const form = event.target.closest('[data-product-form]');

    if (!form) return;

    console.error('Variant change failed:', event.detail.error);

    // Kit's default is to navigate to the failed selection's URL. Quick shop
    // (modal or inline) has nowhere to navigate to — settle as unavailable
    // instead of leaving the shopper on a dead page.
    if (event.target.closest('.js-quick-shop, .inline-quickshop')) {
      event.preventDefault();
      settle(form, event.target, null);
    }
  });
}
